import { defaultPageSelectors, IDefaultPageLayoutProps } from '@opengov/app-core/layout';
import { getEntity } from '@opengov/app-core/entity/selectors';
import { appSelector, selectNavigationBarProps } from '@opengov/app-core/navigation/selectors';
import { selectToast, viewActionCreators } from '@opengov/component-library-classic/module/view';
import {
  selectIsContextMenuOpen,
  selectIsModalOpen,
  selectIsToastOpen
} from '@opengov/component-library-classic/module/view/selectors';
import { createSelector, createStructuredSelector } from '@opengov/compose-app/reselect';
import { getLocation } from '@opengov/compose-app-router/selectors';
import { selectFeatureFlags } from '@opengov/feature-flag/selectors';
import { ShellAuth0Provider } from '@opengov/shell-auth0-react';
import React from 'react';

import { storiesMsgs } from '../../locale';
import { handleOgAssistDrawerToggle } from '../utils/ogAssistSummaryRefresh';

const STORIES_APPLICATION_CONTEXT = [{ key: 'activeApp', label: 'Active App', value: 'stories' }];
import { getModalSelector } from '../../modals';
import { getSession } from '@opengov/app-core/session/selectors';
import { selectIsFormAndWorkflowsFeatureFlagEnabled, selectIsPerformanceManagementFeatureFlagEnabled } from '../common/flags';
import { selectShowGlobalNavBar } from './storyListSelectors';
import { StoriesNavBar } from '../components/StoriesNavBar/StoriesNavBar';
import { storiesRoutePattern } from '../../instrumentation/storiesRoutePattern';

/** Same flag as bnp-ui: gates every PostHog emission from Stories. */
export const selectIsPosthogEnabled: (featureFlags) => boolean = createSelector(
  selectFeatureFlags,
  (featureFlags) => !!featureFlags.get('product-budget-feature-enable-posthog')
);

const selectRoutePattern = createSelector(getLocation, storiesRoutePattern);

export const selectHideClassicReporting: (featureFlags) => boolean = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags.get('product-reporting-hide-classic')
);

export const shouldEnableGlobalNavRebranding: (featureFlags) => boolean = createSelector(
  selectFeatureFlags,
  (featureFlags) => featureFlags.get('product-budget-global-nav-rebranding')
);

declare global {
  interface Window {
    __OG_STORIES_PSP_OPTIONS__?: unknown;
  }
}

// PSP mode: the PSP shell renders the global nav itself. Stories' navbar
// package (@opengov/bnp-component-navbar) constructs its own Auth0Provider
// internally — under PSP, that contends with the shell's Auth0 instance for
// the silent-auth iframe lock and the call times out after 60s (PSS-2542).
//
// We need DefaultPageLayout to render NEITHER the 53px top global-nav padding
// NOR the 70px left navigation sidebar. DefaultPageLayout.tsx derives:
//   withGlobalNavBar = shouldShowGlobalNavBar && globalNavBarRenderer
//   withNavigationBar = !(isUndefined(shouldShowGlobalNavBar) || withGlobalNavBar)
// So:
//   true       → withGlobalNavBar true  → no side bar, but 53px top padding
//   false      → withGlobalNavBar false → side bar appears
//   undefined  → both false             → no side bar, no top padding ✓
// Return undefined for the PSP path; the null renderer below is a
// defense-in-depth no-op.
const isPspMode = (): boolean =>
  typeof window !== 'undefined' && window.__OG_STORIES_PSP_OPTIONS__ != null;

const NullNavBarRenderer = () => null;
NullNavBarRenderer.displayName = 'PspNullNavBarRenderer';

export const selectStoriesPageProps = createStructuredSelector<any, IDefaultPageLayoutProps<any, any>>({
  ...defaultPageSelectors,
  shouldShowGlobalNavBar: createSelector(selectShowGlobalNavBar, (showNavBar) =>
    isPspMode() ? undefined : showNavBar
  ),
  globalNavBarRenderer: createSelector(
    selectHideClassicReporting,
    getSession,
    getEntity,
    appSelector,
    shouldEnableGlobalNavRebranding,
    selectIsFormAndWorkflowsFeatureFlagEnabled,
    selectIsPerformanceManagementFeatureFlagEnabled,
    selectIsPosthogEnabled,
    selectRoutePattern,
    (
      hideClassicReporting,
      session,
      entity,
      apps,
      enableRebrand,
      isFormsAndWorkflowsFeatureEnabled,
      isPerformanceManagementFeatureEnabled,
      isPosthogEnabled,
      routePattern
    ) => {
      if (isPspMode()) {
        return NullNavBarRenderer;
      }
      const auth0domain = window.process.env.AUTH0_DOMAIN;
      const auth0clientID = window.process.env.AUTH0_CLIENT_ID;
      const handleSignOut = () => {
        window.location.href = '/access/logout';
      };
      const notificationsUrl = window.process.env.GLOBAL_NAV_NOTIFICATIONS_URL || undefined;
      const handleViewAllNotificationsClick = () => {
        window.location.href = `/budgeting/${entity.subdomain}/notifications`;
      };
      const navBar = (
        <StoriesNavBar
          session={{
            uuid: session.uuid,
            email: session.email,
            full_name: session.full_name,
            first_name: session.first_name,
            last_name: session.last_name,
            department: session.department
          }}
          entity={{
            uuid: entity.uuid,
            name: entity.name,
            subdomain: entity.subdomain,
            salesforce_id: entity.salesforce_id,
            country: entity.country,
            state: entity.state
          }}
          isPosthogEnabled={isPosthogEnabled}
          isPspMode={false}
          routePattern={routePattern}
          controlPanelMenuItems={apps.toJS()}
          entitySubdomain={entity.subdomain}
          entityUuid={entity.uuid}
          entityFeaturePermissions={entity.feature_permissions}
          user={{
            id: session.id,
            first_name: session.first_name,
            last_name: session.last_name,
            avatar: session.avatar,
            uuid: session.uuid,
            role: session.role.name
          }}
          hideClassicReporting={hideClassicReporting}
          contextSwitcherOptions={{
            baseUrl: '',
            userUUID: session.uuid,
            activeEntityId: entity.id,
            activeEntityIdType: 'BY_FRIENDLY_ID',
          }}
          auth0Config={
            auth0clientID && auth0domain
              ? { auth0domain, auth0clientID, auth0callbackUrl: `${window.location.origin}/auth/users/callback` }
              : undefined
          }
          isFormAndWorkflowsFeatureEnabled={isFormsAndWorkflowsFeatureEnabled}
          isPerformanceManagementFeatureEnabled={isPerformanceManagementFeatureEnabled}
          ogAssistSearchProps={{
            entityFeaturePermissions: entity.feature_permissions,
            entityId: entity.entity_id,
            subdomain: entity.subdomain,
            userUuid: session.uuid
          }}
          handleSignOut={handleSignOut}
          agentsBaseUrl={window.process.env.AGENTS_BASE_URL ?? ''}
          applicationContext={STORIES_APPLICATION_CONTEXT}
          ogAssistDrawerOptions={{ onDrawerToggle: handleOgAssistDrawerToggle }}
          notificationsUrl={notificationsUrl}
          onViewAllNotificationsClick={handleViewAllNotificationsClick}
        />
      );
      // The navbar's platform calls (beacon notifications, entitlements, claims)
      // require an Auth0 Bearer token, but Stories' own Hasura/stories-service calls
      // reject a user Bearer and must keep using the session cookie. This scoped
      // provider gives a token only to the navbar subtree. It deliberately omits
      // useRefreshTokens so getAccessTokenSilently falls through to the silent
      // iframe login (the user's existing Auth0 session); with refresh tokens on
      // and none stored it throws "Missing Refresh Token" instead.
      const globalNavBarRenderer = () =>
        auth0clientID && auth0domain ? (
          <ShellAuth0Provider
            mode="standalone"
            domain={auth0domain}
            clientId={auth0clientID}
            cacheLocation="memory"
            authorizationParams={{
              redirect_uri: window.location.origin,
              audience: 'opengov'
            }}
          >
            {navBar}
          </ShellAuth0Provider>
        ) : (
          navBar
        );
      globalNavBarRenderer.displayName = 'GlobalNavBarRenderer';
      return globalNavBarRenderer;
    }
  ),
  shouldShowModal: createSelector(selectIsModalOpen, defaultPageSelectors.shouldShowModal, (a, b) => a || b),
  showContextMenu: createSelector(selectIsContextMenuOpen, defaultPageSelectors.showContextMenu, (a, b) => a || b),
  isToastOpen: createSelector(selectIsToastOpen, defaultPageSelectors.isToastOpen, (a, b) => a || b),
  toast: createSelector(selectToast, defaultPageSelectors.toast, (a, b) => a || b),
  hideToast: () => () => {
    defaultPageSelectors.hideToast();
    viewActionCreators.hideToast();
  },
  viewElementsOnClick: () => () => {
    defaultPageSelectors.viewElementsOnClick();
    viewActionCreators.documentClicked();
  },
  modalMetaData: getModalSelector,
  navigationBarProps: createSelector(selectNavigationBarProps, (props) => {
    return {
      ...props,
      showAppSwitcher: true,
      activeProduct: 'reporting',
      onLogout: () => (window.location.href = '/access/logout')
    };
  })
});

export const selectStoriesPageMessages = createSelector(storiesMsgs, (msgs) => ({
  title: msgs.storiesPage.title,
  skipTo: msgs.storiesPage.skipTo,
  loading: msgs.storiesPage.loading
}));
